import React, { useEffect } from 'react';
import { InferGetServerSidePropsType } from 'next';
import { MetricsTracker, USER_EVENTS } from '@sm/metrics';
import EnterpriseThankYouComponent from '~app/pages/EnterpriseThankYou';
import routeHandler from '~helpers/routeHandler';
import { buildSmOptions } from '~helpers/buildSmOptions';
import enterpriseThankYou from '~helpers/pages/enterprise-thank-you';
import { EnterpriseThankYouServerSideData } from '~helpers/pages/enterprise-thank-you/index';
import withExperiments from '~helpers/experiments';
import { ExperimentData } from '~helpers/types';
import { Request, Response, LayoutData, CustomGetServerSideProps } from '~helpers/middleware/types';
import { fetchLocaleMessages } from '~helpers/fetchLocaleMessages';
import { useGlobalStyles } from '~app/styles/cmsTheme';
import PageLayout from '~components/pageLayout';
import { PageWithLayout } from '~helpers/pages/types';
import { CONTENTEXP_755, setExperimentCookies } from '~app/pages/experiments';

type ServerSideProps = LayoutData &
  EnterpriseThankYouServerSideData & {
    experiments?: ExperimentData[];
  };

export const getServerSideProps: CustomGetServerSideProps<ServerSideProps> = async ({ req, res, query }) => {
  const handler = routeHandler({
    query,
    pageDataLoader: enterpriseThankYou,
    owners: '@webplatform/surveys',
    onetrustBannerEnabled: true,
  });
  withExperiments([CONTENTEXP_755.identifier], {})(handler);
  await handler.run(req as unknown as Request, res as unknown as Response);
  const smParams = buildSmOptions(query)?.smParams;
  if (!req.pageProps?.fetchServerSideDataSuccess) {
    // TODO: define error handling strategy
    return {
      redirect: {
        destination: '/500',
        permanent: false,
      },
      smParams,
    };
  }

  // TODO: Retire the usage of StaticContext and use page props instead.
  // Additional page-specific (reCAPTCHA) data needed in StaticContext
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore: The enhanced request
  req.addClientPayload('staticData', {
    recaptchaV3: {
      enabled: process?.env?.SECURITY_RECAPTCHA_V3_ENABLED?.toLowerCase() === 'true' ?? false,
      siteKey: process?.env?.SECURITY_RECAPTCHA_V3_SITE_KEY ?? '',
      url: process?.env?.SECURITY_RECAPTCHA_V3_URL ?? '',
      urlChina: process?.env?.SECURITY_RECAPTCHA_V3_URL_CHINA ?? '',
      useEnterprise: process?.env?.SECURITY_RECAPTCHA_V3_USE_ENTERPRISE?.toLowerCase() === 'true' ?? false,
    },
    smParams,
    templateType: 'default',
    contentexp755Treatment: req?.pageProps?.experiments?.[0]?.treatment ?? '',
  });

  const { slLanguageLocale, languageCode } = req.payloads?.staticData?.environment ?? {};
  const translationData = await fetchLocaleMessages(slLanguageLocale, languageCode);

  const pageProps = {
    props: {
      // added to the request via routeHandler
      ...req.payloads,
      // added to the request via the pageDataLoaderFactory
      ...req.pageProps,
      translationData,
      layout: {
        variant: 'SurveyMonkey',
        options: {
          pageId: 'EnterpriseThankYou',
          legacyWeb: 'responseweb',
          includeHeader: false,
          includeFooter: false, // TODO: is this needed?
        },
      },
      experiments: req?.pageProps?.experiments,
    },
  };

  if (!pageProps.props.fetchServerSideDataSuccess) {
    return {
      redirect: {
        destination: '/500',
        permanent: false,
      },
    };
  }

  return pageProps;
};

// Maybe we want to not use InferGetServerSidePropsType here
const EnterpriseThankYou: PageWithLayout<InferGetServerSidePropsType<typeof getServerSideProps>> = ({
  fetchServerSideDataSuccess,
  defaultEndPageSettings,
  premiumCustomFontLink,
  surveyTheme,
  pageOptions,
  smParams,
  experiments,
}): JSX.Element => {
  setExperimentCookies(experiments);
  useGlobalStyles();
  const isDataFetchError = !fetchServerSideDataSuccess || defaultEndPageSettings === null;

  useEffect(() => {
    MetricsTracker.track({
      name: USER_EVENTS.PAGE_VIEW,
      data: {
        amplitudeEvent: 'page viewed',
        pageName: 'enterprise thank you',
        templateType: 'default',
        surveyId: smParams?.surveyId,
        collectorId: smParams?.collectorId,
        respondentId: smParams?.respondentId,
        urlPath: window.location.pathname,
        app: 'respweb',
        log: {
          message: 'enterprise-thank-you',
        },
      },
    });
  }, [smParams]);

  return (
    <EnterpriseThankYouComponent
      defaultEndPageSettings={defaultEndPageSettings}
      premiumCustomFontLink={premiumCustomFontLink}
      surveyTheme={surveyTheme}
      pageOptions={pageOptions}
      isDataFetchError={isDataFetchError}
    />
  );
};

EnterpriseThankYou.getLayout = function getLayout(page) {
  const { layout, staticData, translationData } = page.props;

  return (
    <PageLayout layout={layout} staticData={staticData} translationData={translationData}>
      {page}
    </PageLayout>
  );
};

export default EnterpriseThankYou;
